import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Search, Loader2, Package, ExternalLink, AlertCircle, Pause, Play, RotateCcw, Sparkles, Image as ImageIcon, CheckCircle2, XCircle } from 'lucide-react';
import authService from '../services/AuthService';

import { API_BASE } from '../consts';
const API = (path) => `${API_BASE}/api/amazon${path}`;

const sleep = (ms) => new Promise(r => setTimeout(r, ms));

// ─── Category autocomplete ───────────────────────────────────────────────
const CategoryAutocomplete = ({ value, onSelect }) => {
    const [query, setQuery] = useState('');
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(false);
    const debounceRef = useRef(null);

    const search = useCallback(async (term) => {
        if (term.trim().length < 3) { setResults([]); return; }
        setLoading(true);
        try {
            const r = await authService.authenticatedFetch(API(`/category-search?term=${encodeURIComponent(term)}`));
            const data = await r.json();
            setResults(data.categories || []);
        } catch (e) { console.error(e); }
        finally { setLoading(false); }
    }, []);

    const handleChange = (e) => {
        const v = e.target.value;
        setQuery(v);
        setOpen(true);
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => search(v), 350);
    };

    return (
        <div className="relative">
            <input
                value={value ? value.name : query}
                onChange={handleChange}
                onFocus={() => setOpen(true)}
                placeholder="e.g. Protein Powder, Dog Toys, Yoga Mats..."
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 transition"
            />
            {loading && <Loader2 size={16} className="animate-spin absolute right-4 top-3.5 text-gray-400" />}
            {open && results.length > 0 && (
                <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-xl max-h-72 overflow-y-auto">
                    {results.map(c => (
                        <button
                            key={c.categoryId}
                            onClick={() => { onSelect(c); setQuery(''); setOpen(false); }}
                            className="w-full text-left px-4 py-2.5 hover:bg-amber-50 transition flex items-center justify-between gap-3 border-b border-gray-50 last:border-0"
                        >
                            <span className="text-sm text-gray-800">{c.name}</span>
                            <span className="text-xs text-gray-400 font-mono shrink-0">{c.productCount?.toLocaleString()} products</span>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

// ─── Status pill ──────────────────────────────────────────────────────────
const Pill = ({ ok, label }) => (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold
    ${ok ? 'bg-red-50 text-red-600 border border-red-200' : 'bg-green-50 text-green-600 border border-green-200'}`}>
        {ok ? <XCircle size={11} /> : <CheckCircle2 size={11} />}
        {label}
    </span>
);

// ─── Generate Proposal button ─────────────────────────────────────────────
const GenerateProposalButton = ({ row }) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const handleGenerate = async () => {
        setLoading(true);
        setError('');
        try {
            const r = await authService.authenticatedFetch(API('/generate-proposal'), {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(row),
            });
            if (!r.ok) {
                const data = await r.json().catch(() => ({}));
                throw new Error(data.error || 'Failed to generate proposal');
            }
            const html = await r.text();
            const blob = new Blob([html], { type: 'text/html' });
            const url = URL.createObjectURL(blob);
            window.open(url, '_blank');
        } catch (e) {
            setError(e.message || 'Failed to generate proposal');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div>
            <button
                onClick={handleGenerate}
                disabled={loading}
                className="px-3 py-1.5 bg-gradient-to-r from-amber-500 to-orange-600 text-white text-xs font-semibold rounded-lg hover:opacity-90 transition flex items-center gap-1.5 disabled:opacity-60"
            >
                {loading ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
                {loading ? 'Building...' : 'Generate Proposal'}
            </button>
            {error && <p className="text-[10px] text-red-500 mt-1 max-w-[140px]">{error}</p>}
        </div>
    );
};

// ─── Result row ───────────────────────────────────────────────────────────
const BrandRow = ({ row }) => {
    if (!row.scrape_ok) {
        return (
            <tr className="border-b border-gray-100">
                <td colSpan={6} className="px-4 py-3 text-sm text-red-500 flex items-center gap-2">
                    <AlertCircle size={14} /> {row.asin} — failed to scrape ({row.error || 'unknown error'})
                </td>
            </tr>
        );
    }

    return (
        <tr className="border-b border-gray-100 hover:bg-amber-50/40 transition">
            <td className="px-4 py-3">
                <div className="w-14 h-14 rounded-lg bg-gray-50 border border-gray-100 overflow-hidden flex items-center justify-center">
                    {row.image
                        ? <img src={row.image} alt="" className="w-full h-full object-contain" />
                        : <ImageIcon size={18} className="text-gray-300" />}
                </div>
            </td>
            <td className="px-4 py-3">
                <a href={row.url} target="_blank" rel="noopener noreferrer" className="text-xs font-mono text-gray-500 hover:text-amber-600 flex items-center gap-1">
                    {row.asin} <ExternalLink size={11} />
                </a>
            </td>
            <td className="px-4 py-3">
                <p className="text-sm font-semibold text-gray-800">{row.brand_name}</p>
                <p className="text-[11px] text-gray-400 truncate max-w-[180px]">{row.byline_raw}</p>
            </td>
            <td className="px-4 py-3">
                <div className="flex flex-wrap gap-1.5">
                    {row.missing?.length
                        ? row.missing.map(m => <Pill key={m} ok label={m} />)
                        : <span className="text-xs text-gray-400">Nothing obvious — skip</span>}
                </div>
            </td>
            <td className="px-4 py-3 text-xs text-gray-400">
                {/* placeholders — not yet wired */}
                <div className="space-y-0.5">
                    <p>Brand Revenue: <span className="text-gray-300">—</span></p>
                    <p>3rd Party Revenue: <span className="text-gray-300">—</span></p>
                    <p>SKUs: <span className="text-gray-300">—</span></p>
                    <p>Sold/mo: <span className="text-gray-300">—</span></p>
                    <p>MORE DATA...</p>
                </div>
            </td>
            <td className="px-4 py-3">
                <GenerateProposalButton row={row} />
            </td>
        </tr>
    );
};

// ─── MAIN PAGE ────────────────────────────────────────────────────────────
const BrandHunterPage = () => {
    const [category, setCategory] = useState(null);
    const [salesMin, setSalesMin] = useState('200');
    const [salesMax, setSalesMax] = useState('3000');

    const [searching, setSearching] = useState(false);
    const [searchError, setSearchError] = useState('');
    const [totalResults, setTotalResults] = useState(0);
    const [maxReachable, setMaxReachable] = useState(0);

    const [asinQueue, setAsinQueue] = useState([]);
    const [rows, setRows] = useState([]);
    const [running, setRunning] = useState(false);
    const [paused, setPaused] = useState(false);
    const stopRef = useRef(false);

    const handleSearch = async () => {
        if (!category) return;
        setSearching(true);
        setSearchError('');
        setRows([]);
        setAsinQueue([]);
        try {
            // pull page 0 then page 1 => up to 10k, per the 5000-perPage cap
            const r0 = await authService.authenticatedFetch(
                API(`/category-asins?category_id=${category.categoryId}&sales_min=${salesMin}&sales_max=${salesMax}&page=0`)
            );
            const d0 = await r0.json();
            if (d0.error) throw new Error(d0.error);

            let combined = d0.asins || [];
            setTotalResults(d0.totalResults || 0);
            setMaxReachable(d0.maxReachable || 0);

            if ((d0.totalResults || 0) > 5000) {
                const r1 = await authService.authenticatedFetch(
                    API(`/category-asins?category_id=${category.categoryId}&sales_min=${salesMin}&sales_max=${salesMax}&page=1`)
                );
                const d1 = await r1.json();
                if (!d1.error) combined = combined.concat(d1.asins || []);
            }

            setAsinQueue(combined);
        } catch (e) {
            setSearchError(e.message || 'Search failed');
        } finally {
            setSearching(false);
        }
    };

    // ── auto-running 3-at-a-time scrape loop ──────────────────────────────
    const runLoop = useCallback(async (queue) => {
        setRunning(true);
        stopRef.current = false;
        let remaining = [...queue];

        while (remaining.length > 0) {
            if (stopRef.current) break;
            const batch = remaining.slice(0, 3);
            remaining = remaining.slice(3);

            try {
                const r = await authService.authenticatedFetch(API('/scrape-asin-batch'), {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ asins: batch }),
                });
                const data = await r.json();
                setRows(prev => [...prev, ...(data.results || [])]);
            } catch (e) {
                console.error('batch error', e);
            }

            setAsinQueue(remaining);
            await sleep(150); // tiny breather between batches
        }

        setRunning(false);
    }, []);

    const handleStart = () => {
        if (!asinQueue.length) return;
        runLoop(asinQueue);
    };

    const handleStop = () => { stopRef.current = true; setRunning(false); };

    const processedCount = rows.length;
    const queueRemaining = asinQueue.length;

    return (
        <div className="min-h-screen bg-gradient-to-br from-amber-50 to-orange-50 pb-16">
            <div className="max-w-6xl mx-auto px-4 pt-10">
                {/* Header */}
                <div className="mb-8">
                    <div className="flex items-center gap-3 mb-1">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center shadow-lg">
                            <Search className="text-white" size={20} />
                        </div>
                        <div>
                            <h1 className="text-3xl font-bold text-gray-900">Brand Hunter</h1>
                            <p className="text-sm text-gray-500">Find brands with weak Amazon presence — your next direct agency clients</p>
                        </div>
                    </div>
                </div>

                {/* Search panel */}
                <div className="bg-white rounded-3xl shadow-xl p-8 border border-gray-100 mb-6">
                    <div className="grid md:grid-cols-3 gap-4 mb-4">
                        <div className="md:col-span-2">
                            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5 block">Category</label>
                            <CategoryAutocomplete value={category} onSelect={setCategory} />
                        </div>
                        <div>
                            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5 block">Monthly sold range</label>
                            <div className="flex items-center gap-2">
                                <input
                                    value={salesMin}
                                    onChange={e => setSalesMin(e.target.value.replace(/\D/g, ''))}
                                    className="w-full border border-gray-200 rounded-xl px-3 py-3 text-sm text-center focus:outline-none focus:ring-2 focus:ring-amber-400"
                                />
                                <span className="text-gray-300">–</span>
                                <input
                                    value={salesMax}
                                    onChange={e => setSalesMax(e.target.value.replace(/\D/g, ''))}
                                    className="w-full border border-gray-200 rounded-xl px-3 py-3 text-sm text-center focus:outline-none focus:ring-2 focus:ring-amber-400"
                                />
                            </div>
                        </div>
                    </div>

                    {category && (
                        <p className="text-xs text-gray-400 mb-4">Selected: <span className="font-semibold text-amber-600">{category.name}</span></p>
                    )}

                    <button
                        onClick={handleSearch}
                        disabled={!category || searching}
                        className="px-6 py-3 bg-gradient-to-r from-amber-500 to-orange-600 text-white rounded-xl font-semibold text-sm hover:opacity-90 transition shadow-lg disabled:opacity-40 flex items-center gap-2"
                    >
                        {searching ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />}
                        Find Products
                    </button>

                    {searchError && (
                        <p className="text-sm text-red-500 mt-3 flex items-center gap-1.5"><AlertCircle size={14} /> {searchError}</p>
                    )}

                    {!searching && asinQueue.length > 0 && (
                        <div className="mt-5 pt-5 border-t border-dashed border-gray-200 flex items-center justify-between flex-wrap gap-3">
                            <p className="text-sm text-gray-600">
                                <span className="font-semibold text-gray-900">{totalResults.toLocaleString()}</span> products match.
                                We can reach <span className="font-semibold text-gray-900">{maxReachable.toLocaleString()}</span> of them
                                ({asinQueue.length.toLocaleString()} queued).
                            </p>
                            <div className="flex gap-2">
                                {!running ? (
                                    <button onClick={handleStart} className="flex items-center gap-1.5 px-5 py-2.5 bg-gray-900 text-white rounded-xl font-semibold text-sm hover:bg-gray-700 transition">
                                        <Play size={14} /> {processedCount > 0 ? 'Resume' : 'Start'} scraping (3 at a time)
                                    </button>
                                ) : (
                                    <button onClick={handleStop} className="flex items-center gap-1.5 px-5 py-2.5 bg-red-500 text-white rounded-xl font-semibold text-sm hover:bg-red-600 transition">
                                        <Pause size={14} /> Stop
                                    </button>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {/* Progress */}
                {(rows.length > 0 || running) && (
                    <div className="flex items-center gap-3 mb-4 text-sm text-gray-500">
                        {running && <Loader2 size={14} className="animate-spin text-amber-500" />}
                        <span>Scraped {processedCount} {totalResults ? `/ up to ${maxReachable.toLocaleString()}` : ''} — {queueRemaining} remaining in queue</span>
                        {!running && queueRemaining > 0 && (
                            <button onClick={handleStart} className="text-amber-600 font-semibold hover:underline flex items-center gap-1">
                                <RotateCcw size={12} /> Continue
                            </button>
                        )}
                    </div>
                )}

                {/* Results table */}
                {rows.length > 0 && (
                    <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
                        <table className="w-full text-left">
                            <thead>
                                <tr className="bg-gray-50 border-b border-gray-100">
                                    <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Image</th>
                                    <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">ASIN</th>
                                    <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Brand</th>
                                    <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">What's missing</th>
                                    <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Brand data</th>
                                    <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((row, i) => <BrandRow key={`${row.asin}-${i}`} row={row} />)}
                            </tbody>
                        </table>
                    </div>
                )}

                {!searching && category && asinQueue.length === 0 && rows.length === 0 && !searchError && (
                    <div className="text-center py-16 text-gray-400 text-sm">Pick a category and sales range, then search.</div>
                )}
            </div>
        </div>
    );
};

export default BrandHunterPage;